import React from 'react';
import { GovFlex } from '@gov-design-system-ce/react';
import type { WizardFormStep } from '@gov-nx/core/hooks';
import { isFileUploadItemWeb } from '@gov-nx/core/hooks';
import { useLocale } from '@gov-nx/core/service';
import type { FormProblemData, PageCode } from '@gov-nx/module/page';
import { usePodporaContext } from '@gov-nx/module/page';
import { Button, ButtonGroupLayout, LayoutGap } from '@gov-nx/ui/web';
import { formatPhoneNumber } from '@gov-nx/utils/common';
import { WizardRecapHeader } from '../../Wizard/WizardRecapHeader';

interface PodporaRecapitulationProps {
	code: PageCode;
	isProcessing: boolean;
	formMethods: WizardFormStep<FormProblemData>['formDefinition'];
}

export const SupportRecapitulation = ({
	code,
	isProcessing,
	formMethods,
}: PodporaRecapitulationProps) => {
	const { wizard, isLogged } = usePodporaContext();
	const { t } = useLocale(code);

	return (
		<LayoutGap gap="xl">
			<LayoutGap gap="m">
				<GovFlex gap="s">
					<WizardRecapHeader
						title={t('formular.pole.udaje-uzivatele')}
						wizard={wizard}
						stepIndex={0}
						strings={{
							wcagLabel: t(
								'formular.tlacitko.wcag.upravit-udaje-o-odesilateli'
							),
							label: t('formular.tlacitko.upravit'),
						}}
					/>
				</GovFlex>
				<dl className="flex flex-col gap-2">
					{!isLogged && (
						<>
							<div>
								<dt className="inline mr-1! font-bold">
									{t('formular.pole.rekapitulace-jmeno')}:
								</dt>
								<dd className="inline">{wizard.formData.jmeno}</dd>
							</div>
							<div>
								<dt className="inline mr-1! font-bold">
									{t('formular.pole.rekapitulace-prijmeni')}:
								</dt>
								<dd className="inline">{wizard.formData.prijmeni}</dd>
							</div>
						</>
					)}
					<div>
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-email')}:
						</dt>
						<dd className="inline">{wizard.formData.email}</dd>
					</div>
					<div>
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-telefon')}:
						</dt>
						<dd className="inline">
							{wizard.formData.telefon
								? formatPhoneNumber(wizard.formData.telefon)
								: t('formular.pole.rekapitulace-telefon-neuvedeno')}
						</dd>
					</div>
				</dl>
			</LayoutGap>
			<LayoutGap gap="m">
				<GovFlex gap="s">
					<WizardRecapHeader
						title={t('formular.pole.rekapitulace-popis-problemu')}
						wizard={wizard}
						stepIndex={1}
						strings={{
							wcagLabel: t('formular.tlacitko.wcag.upravit-popis-problemu'),
							label: t('formular.tlacitko.upravit'),
						}}
					/>
				</GovFlex>

				<dl className="flex flex-col gap-2">
					<div>
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-nazev-problemu')}:
						</dt>
						<dd className="inline">{wizard.formData.souhrn}</dd>
					</div>
					<div>
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-detailni-popis')}
						</dt>
						<dd className="inline">{wizard.formData.podrobnyPopis}</dd>
					</div>
					<div>
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-vyskyt-chyby')}:
						</dt>
						<dd className="inline">
							{t('formular.pole.seznam.' + wizard.formData.vyskytChyby)}
						</dd>
					</div>
					<div>
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-drivejsi-stav')}:
						</dt>
						<dd className="inline">
							{t('formular.pole.seznam.' + wizard.formData.drivejsiStav)}
						</dd>
					</div>
					{wizard.formData.zpusobPrihlaseni && (
						<div>
							<dt className="inline mr-1! font-bold">
								{t('formular.pole.rekapitulace-zpusob-prihlaseni-nazev')}:
							</dt>
							<dd className="inline">
								{t(
									'formular.pole.rekapitulace-zpusob-prihlaseni.' +
										wizard.formData.zpusobPrihlaseni
								)}
							</dd>
						</div>
					)}
					{formMethods.formMethods.watch('zpusobPrihlaseni') ===
						'JMENO_HESLO' && (
						<>
							<div>
								<dt className="inline mr-1! font-bold">
									{t('formular.pole.rekapitulace-zpusob-prihlaseni-username')}:
								</dt>
								<dd className="inline">{wizard.formData.prihlaseniUserName}</dd>
							</div>

							<div>
								<dt className="inline mr-1! font-bold">
									{t('formular.pole.rekapitulace-zpusob-prihlaseni-email')}:
								</dt>
								<dd className="inline">{wizard.formData.prihlaseniEmail}</dd>
							</div>

							<div>
								<dt className="inline mr-1! font-bold">
									{t('formular.pole.rekapitulace-zpusob-prihlaseni-telefon')}:
								</dt>
								<dd className="inline">
									{wizard.formData.prihlaseniTelefon
										? formatPhoneNumber(wizard.formData.prihlaseniTelefon)
										: '-'}
								</dd>
							</div>
						</>
					)}
					{formMethods.formMethods.watch('zpusobPrihlaseni') === 'DS' && (
						<div>
							<dt className="inline mr-1! font-bold">
								{t('formular.pole.rekapitulace-zpusob-prihlaseni-id-DS')}:
							</dt>
							<dd className="inline">{wizard.formData.prihlaseniIdDS}</dd>
						</div>
					)}

					<div className="flex">
						<dt className="inline mr-1! font-bold">
							{t('formular.pole.rekapitulace-priloha')}:
						</dt>
						<dd className="flex-1">
							{wizard.formData['priloha'] && wizard.formData['priloha'].length
								? wizard.formData['priloha']
										.filter(isFileUploadItemWeb)
										.map((attachment) => (
											<Button
												key={attachment.id}
												color="primary"
												type="link"
												className="wrap-normal"
												onClick={() =>
													window.open(URL.createObjectURL(attachment.file))
												}
												aria-label={attachment.name}>
												{attachment.name}
											</Button>
										))
								: t('formular.pole.rekapitulace-bez-prilohy')}
						</dd>
					</div>
				</dl>
			</LayoutGap>
			<ButtonGroupLayout>
				<Button
					isLoading={isProcessing}
					aria-label={t('formular.wcag.odeslat')}
					isDisabled={isProcessing}
					nativeType="submit"
					color="primary"
					size="l"
					type="solid">
					{t('formular.tlacitko.odeslat')}
				</Button>
			</ButtonGroupLayout>
		</LayoutGap>
	);
};
